import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `
Política de Privacidad — El Sistema Blindado

Última actualización: Marzo 2026

1. Información que Recopilamos
Recopilamos únicamente la información necesaria para procesar tu compra, incluyendo tu nombre, dirección de correo electrónico y datos de pago. No almacenamos información de tarjetas de crédito directamente; todos los pagos son procesados por plataformas de pago seguras de terceros.

2. Uso de la Información
Tu información personal se utiliza exclusivamente para:
• Procesar y entregar tu compra digital.
• Enviarte actualizaciones relacionadas con el producto adquirido.
• Responder a consultas de soporte.

3. Protección de Datos
Implementamos medidas de seguridad estándar de la industria para proteger tu información personal contra acceso no autorizado, alteración o destrucción.

4. Compartir Información
No vendemos, intercambiamos ni transferimos tu información personal a terceros, excepto a proveedores de servicios de confianza que nos asisten en la operación del sitio web y el procesamiento de pagos, siempre que dichas partes acuerden mantener esta información confidencial.

5. Cookies
Nuestro sitio puede utilizar cookies para mejorar la experiencia del usuario. Puedes configurar tu navegador para rechazar cookies, aunque esto podría afectar algunas funcionalidades del sitio.

6. Tus Derechos
Tienes derecho a solicitar acceso, corrección o eliminación de tus datos personales en cualquier momento enviando una solicitud por escrito.

7. Cambios a esta Política
Nos reservamos el derecho de actualizar esta política en cualquier momento. Los cambios serán publicados en esta página con la fecha de actualización correspondiente.
`.trim();
const termsContent = `
Términos y Condiciones — El Sistema Blindado

Última actualización: Marzo 2026

1. Aceptación de los Términos
Al adquirir y utilizar "El Sistema Blindado", aceptas estos términos y condiciones en su totalidad. Si no estás de acuerdo con alguna parte, no debes utilizar este producto.

2. Naturaleza del Producto
"El Sistema Blindado" es un producto digital de carácter educativo e informativo. NO constituye asesoramiento legal, financiero ni profesional. La información proporcionada se basa en leyes federales de protección al consumidor de los Estados Unidos, pero no reemplaza la consulta con un profesional licenciado.

3. Resultados
Los resultados pueden variar significativamente según la situación individual de cada usuario. No garantizamos resultados específicos en cuanto a mejoras en el puntaje de crédito, eliminación de deudas o cualquier otro resultado financiero.

4. Uso Permitido
El producto es para uso personal y exclusivo del comprador. Queda prohibida la reproducción, distribución, reventa o compartición del contenido sin autorización expresa por escrito.

5. Propiedad Intelectual
Todo el contenido, incluyendo textos, plantillas, guiones y estrategias, es propiedad intelectual de El Sistema Blindado y está protegido por las leyes de derechos de autor aplicables.

6. Política de Reembolso
Debido a la naturaleza digital del producto y su entrega inmediata, todas las ventas son finales. No se ofrecen reembolsos una vez que se ha accedido al contenido.

7. Limitación de Responsabilidad
El Sistema Blindado, sus creadores y afiliados no serán responsables por daños directos, indirectos, incidentales o consecuentes que resulten del uso o la incapacidad de usar el producto.

8. Ley Aplicable
Estos términos se rigen por las leyes de los Estados Unidos de América. Cualquier disputa será resuelta en los tribunales competentes.

9. Modificaciones
Nos reservamos el derecho de modificar estos términos en cualquier momento. El uso continuado del producto después de cualquier cambio constituye la aceptación de los nuevos términos.
`.trim();
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-navy-950 py-8 text-center text-gray-500 text-sm border-t border-navy-800" data-id="element-31">
        <div className="max-w-4xl mx-auto px-4" data-id="element-32">
          <p className="mb-3" data-id="element-33">
            © {new Date().getFullYear()} El Sistema Blindado. Todos los
            derechos reservados.
          </p>
          <p className="text-xs opacity-70 max-w-2xl mx-auto mb-4" data-id="element-34">
            Descargo de responsabilidad: Este producto proporciona información
            educativa sobre la reparación de crédito y no constituye
            asesoramiento legal. Los resultados pueden variar según la situación
            individual.
          </p>
          <div className="flex items-center justify-center gap-4" data-id="element-35">
            <button onClick={() => setActiveModal('privacy')} className="text-gray-500 hover:text-gold-500 transition-colors underline underline-offset-2 cursor-pointer" data-id="element-36">
              Política de Privacidad
            </button>
            <span className="text-gray-700" data-id="element-37">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-gray-500 hover:text-gold-500 transition-colors underline underline-offset-2 cursor-pointer" data-id="element-38">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-39">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-40">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3
        }} className="bg-navy-800 border border-navy-800 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-41">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-navy-900" data-id="element-42">
                <h3 className="font-serif text-xl font-bold text-white" data-id="element-43">
                  {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h3>
                <button onClick={() => setActiveModal(null)} className="text-gray-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-navy-900 cursor-pointer" data-id="element-44">
                  <XIcon className="w-5 h-5" data-id="element-45" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)] text-gray-300 text-sm leading-relaxed whitespace-pre-line" data-id="element-46">
                {activeModal === 'privacy' ? privacyContent : termsContent}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}